import {
  Table,
  TableHead,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
} from "V2.0/common/table";
import { Badge } from "V2.0/common/badge";
import UserProfile from "./UserProfile";
import UserProfileActions from "./UserProfileActions";
import {
  ApplicationSection,
  AssistantSection,
  UserAccessLevel,
} from "V2.0/models/UserProfile";
import clsx from "clsx";

const userAccessLevelBadge = {
  OWNER: <Badge color="lime">Owner</Badge>,
  WHITELABELUSER: <Badge color="zinc">User</Badge>,
  WHITELABELOWNER: <Badge color="lime">Owner</Badge>,
  WHITELABELVIEWER: <Badge color="blue">Viewer</Badge>,
  WHITELABELOPERATOR: <Badge color="amber">Operator</Badge>,
};

const fullPermissions = <Badge color="red">Full</Badge>;
const noPermissions = <Badge color="purple">None</Badge>;

const viewPermissions = <Badge color="emerald">View</Badge>;
const modifyPermissions = <Badge color="amber">Modify</Badge>;

function SectionPermissions(props) {
  const { userAccessLevel, visibleSections } = props;

  if (userAccessLevel != UserAccessLevel.WHITELABELOPERATOR) {
    return fullPermissions;
  }

  if (visibleSections.length > 0) {
    const sectionBadges = visibleSections.map((section, idx) => (
      <div className={clsx(idx === 0 && "mt-2 ml-2")}>
        <Badge color="blue">{section}</Badge>
      </div>
    ));

    return (
      <div className="flex flex-wrap max-w-80 space-y-2 space-x-2">
        {sectionBadges}
      </div>
    );
  }

  return noPermissions;
}

function Permissions(props) {
  const { userAccessLevel } = props;

  if (userAccessLevel === UserAccessLevel.WHITELABELVIEWER) {
    return viewPermissions;
  }

  return (
    <div className="flex flex-wrap space-x-2 space-y-2 items-center">
      <div className="flex-none mt-2">{viewPermissions}</div>
      <div className="flex-none">{modifyPermissions}</div>
    </div>
  );
}

export default function UserProfilesTable(props) {
  const {
    userProfiles,
    onManageWorkspaceAccess,
    onModifyUserRole,
    onDeactivateUser
  } = props;

  return (
    <Table>
      <TableHead>
        <TableHeader>User</TableHeader>
        <TableHeader>Role</TableHeader>
        <TableHeader>Permissions</TableHeader>
        <TableHeader>App Sections</TableHeader>
        <TableHeader>Assistant Sections</TableHeader>
        <TableHeader>Status</TableHeader>
        <TableHeader>Actions</TableHeader>
      </TableHead>
      <TableBody>
        {userProfiles.map((userProfile) => {
          const {
            firstName,
            lastName,
            isCompanyOwner,
            user,
            active,
            userAccessLevel,
            applicationSectionsVisible,
            assistantSectionsVisible,
          } = userProfile;
          const { email, _id } = user || { email: "", _id: "" };
          const _userAccessLevel =
            userAccessLevel ?? (isCompanyOwner ? "WHITELABELOWNER" : "WHITELABELUSER");

          return (
            <TableRow key={_id}>
              <TableCell>
                <UserProfile
                  firstName={firstName}
                  lastName={lastName}
                  email={email}
                  active={active}
                />
              </TableCell>
              <TableCell>{userAccessLevelBadge[_userAccessLevel]}</TableCell>
              <TableCell>
                <Permissions userAccessLevel={userAccessLevel} />
              </TableCell>
              <TableCell>
                <SectionPermissions
                  userAccessLevel={_userAccessLevel}
                  visibleSections={
                    applicationSectionsVisible ??
                    Object.values(ApplicationSection)
                  }
                />
              </TableCell>
              <TableCell>
                <SectionPermissions
                  userAccessLevel={_userAccessLevel}
                  visibleSections={
                    assistantSectionsVisible ?? Object.values(AssistantSection)
                  }
                />
              </TableCell>
              <TableCell>
                {active ? (
                  <Badge color="green">Active</Badge>
                ) : (
                  <Badge color="red">Deactivated</Badge>
                )}
              </TableCell>
              <TableCell>
                {!isCompanyOwner && user.userAccessLevel !== "OWNER" && Boolean(user) && active && (
                  <UserProfileActions
                    onManageWorkspaceAccess={() =>
                      onManageWorkspaceAccess({ ...user, firstName, lastName })
                    }
                    onModifyUserRole={() => onModifyUserRole(userProfile)}
                    onDeactivateUser={() => onDeactivateUser({ ...user, firstName, lastName })}
                  />
                )}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
